import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { invalidateFeaturesCache } from '@/lib/useFeatures';
import { Sparkles, Flame, BookOpen, Radio, Camera, CheckCircle2, AlertCircle } from 'lucide-react';

const META = {
  cinematic_read: { Icon: BookOpen, accent: 'text-[color:var(--kc-gold)]' },
  forge: { Icon: Flame, accent: 'text-orange-400' },
  oracle: { Icon: Sparkles, accent: 'text-[color:var(--kc-gold)]' },
  pirate_radio: { Icon: Radio, accent: 'text-fuchsia-400' },
  proof_of_life: { Icon: Camera, accent: 'text-emerald-400' },
};

const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} className="inline-flex items-center gap-2" data-testid={testid}>
    <span className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-[color:var(--kc-gold)]' : 'bg-gray-300'}`}>
      <span className={`absolute top-0.5 ${checked ? 'left-6' : 'left-0.5'} w-5 h-5 bg-white rounded-full shadow transition-all`} />
    </span>
  </button>
);

export default function FeaturesManager() {
  const [features, setFeatures] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const { data } = await api.get('/admin/settings/features');
      setFeatures(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load features');
    }
  };
  useEffect(() => { load(); }, []);

  const patchFeature = async (slug, patch) => {
    if (!features) return;
    setSaving(true);
    setError('');
    // Optimistic update.
    const prev = features;
    const next = { ...features, [slug]: { ...features[slug], ...patch } };
    setFeatures(next);
    try {
      const { data } = await api.put('/admin/settings/features', { [slug]: patch });
      setFeatures(data);
      invalidateFeaturesCache();
      setSavedMsg('Saved.');
      setTimeout(() => setSavedMsg(''), 1800);
    } catch (e) {
      setFeatures(prev);
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!features) return <div className="text-muted-kc text-sm" data-testid="features-loading">Loading feature flags…</div>;

  return (
    <div className="space-y-6" data-testid="features-manager">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="font-display uppercase text-3xl md:text-4xl">Signature features</h2>
          <p className="text-muted-kc text-sm mt-1">Toggle each flagship feature on or off across the whole site. Off = the route, navigation entry, and API are all blocked.</p>
        </div>
        {savedMsg && <span className="text-sm text-[color:var(--kc-success)] inline-flex items-center gap-1"><CheckCircle2 size={14}/> {savedMsg}</span>}
      </div>

      {error && (
        <div className="bg-red-50 border border-[color:var(--kc-danger)] text-[color:var(--kc-danger)] rounded-[var(--kc-radius-sm)] p-3 text-sm inline-flex items-center gap-2" data-testid="features-error">
          <AlertCircle size={14}/> {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(features).map(([slug, f]) => {
          const meta = META[slug] || { Icon: Sparkles, accent: 'text-[color:var(--kc-gold)]' };
          const Icon = meta.Icon;
          return (
            <div key={slug} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5 flex flex-col gap-3" data-testid={`feature-card-${slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className={`mt-0.5 ${meta.accent}`}><Icon size={20}/></span>
                  <div>
                    <div className="font-display uppercase text-xl leading-tight">{f.label || slug}</div>
                    <div className="text-xs text-muted-kc mt-1">{f.description || ''}</div>
                  </div>
                </div>
                <Toggle checked={!!f.enabled} onChange={(v) => patchFeature(slug, { enabled: v })} testid={`feature-toggle-${slug}`} />
              </div>
              {/* Per-feature extra controls */}
              {slug === 'proof_of_life' && (
                <div className="flex items-center justify-between pt-3 border-t border-kc text-sm">
                  <span className="text-ink-2">Allow users to share their 90-day mosaic</span>
                  <Toggle checked={!!f.allow_share} onChange={(v) => patchFeature(slug, { allow_share: v })} testid={`feature-toggle-${slug}-share`} />
                </div>
              )}
              <div className="flex items-center justify-between pt-2 text-xs uppercase tracking-wider">
                <span className={f.enabled ? 'text-[color:var(--kc-success)] font-semibold' : 'text-muted-kc font-semibold'}>
                  {f.enabled ? 'ON · Visible to users' : 'OFF · Hidden everywhere'}
                </span>
                {saving && <span className="text-muted-kc">Saving…</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
